import React, { useState, useEffect } from 'react';
import HomeImage from '../assets/homeImage.jpg';
import MHELogo from '../assets/MHELogo.svg';
import '../css/main.css';
import "../css/style.css";
import "../css/bootstrap.min.css";
import 'bootstrap-icons/font/bootstrap-icons.css';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import OwlCarousel from 'react-owl-carousel';
import fallbackImage from '../assets/fallback.jpg';
import SeoHead from './Shared/SeoHead';

const Home = () => {
    const siteUrl = window.location.origin;
    const [products, setProducts] = useState([]);
    const [content, setContent] = useState([]);
    const [imageDirectory, setImageDirectory] = useState('');
    const [docsDirectory, setDocsDirectory] = useState('');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const bearerToken = localStorage.getItem('bearerToken');
    const navigate = useNavigate();

    useEffect(() => {
        const initialize = async () => {
            try {
                await authenticateUser();

                const [dirs, cats, productsList] = await Promise.all([
                    getDirectories(),
                    fetchCategories(),
                    fetchProducts()
                ]);

                await runImageCheck();

                setImageDirectory(dirs?.imgDirectory || '');
                setDocsDirectory(dirs?.docsDirectory || '');
                setCategories(Array.isArray(cats) ? cats : []);
                setProducts(Array.isArray(productsList) ? productsList : []);
                setContent(Array.isArray(productsList) ? productsList : []);
            } catch (error) {
                console.error('Initialization error:', error);
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, []);

    const authenticateUser = async () => {
        try {
            await axios.get("/Website/MHEWebsiteLogin", {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${bearerToken}`,
                },
            });
        } catch (error) {
            console.error('Authentication error:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('/Website/GetCategories', {
                headers: { Authorization: `Bearer ${bearerToken}` },
            });
            return response.data || [];
        } catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axios.get('/Website/getProducts', {
                headers: { Authorization: `Bearer ${bearerToken}` },
            });
            return response.data || [];
        } catch (error) {
            console.error('Error fetching products:', error);
            return [];
        }
    };

    const runImageCheck = async () => {
        try {
            await axios.get("/Website/CheckAndCopyBlobStorageToWebsite", {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${bearerToken}`,
                },
            });
        } catch (error) {
            console.error('Image check error:', error);
        }
    };

    const getDirectories = async () => {
        try {
            const response = await axios.get("/Website/GetDirectories", {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${bearerToken}`,
                },
            });
            return response.data || { imgDirectory: '', docsDirectory: '' };
        } catch (error) {
            console.error('Error fetching directories:', error);
            return { imgDirectory: '', docsDirectory: '' };
        }
    };

    const navToPage = (page, category) => {
        if (!Array.isArray(products) || !Array.isArray(categories)) return;

        const sortedProducts = [...products].sort((a, b) => {
            const aHasNx = a?.proD_NAME?.toLowerCase().includes("nx") ? 0 : 1;
            const bHasNx = b?.proD_NAME?.toLowerCase().includes("nx") ? 0 : 1;
            return aHasNx - bHasNx || (a?.proD_NAME || '').localeCompare(b?.proD_NAME || '');
        });

        navigate(page, { state: { cat: categories, products: sortedProducts, selCat: category } });
    };

    const sortedFeaturedProducts = Array.isArray(products)
        ? [...products]
            .filter(p => p?.proD_NAME)
            .sort((a, b) => {
                const aHasNX = a.proD_NAME.includes("NX") ? 1 : 0;
                const bHasNX = b.proD_NAME.includes("NX") ? 1 : 0;
                return bHasNX - aHasNX;
            })
        : [];

    const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'MHE Electronics',
        url: siteUrl,
        logo: `${siteUrl}/MHELogo.svg`,
        email: 'sales@mhe.co.za',
        telephone: '+27 31 780 2560',
        description: 'MHE Electronics is the original developer and manufacturer of electronic control systems, warning equipment, and custom industrial electronics.',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '49 Alexander Road, Westmead',
            addressLocality: 'Pinetown',
            addressRegion: 'KwaZulu-Natal',
            postalCode: '3608',
            addressCountry: 'ZA'
        },
        sameAs: [
            'https://www.mhe.co.za/'
        ]
    };

    return (
        <div className="home-page">
            <SeoHead
                title="MHE Electronics | Original Developer and Manufacturer"
                description="MHE Electronics designs and manufactures electronic control systems, warning equipment, and custom OEM electronics from South Africa."
                canonical={`${siteUrl}/`}
                schema={organizationSchema}
            />

            {/* ── Hero Section ── */}
            <section className="hero-section">
                <div className="hero-overlay" />
                <img src={HomeImage} alt="MHE Electronics" className="hero-bg" />
                <div className="hero-content">
                    <img src={MHELogo} alt="MHE Logo" className="hero-logo" />
                    <p className="hero-subtitle">
                        Precision Electronics &amp; Industrial Solutions
                    </p>
                    <div className="hero-actions">
                        <button className="hero-btn-primary" onClick={() => navToPage('/Products', 0)}>
                            <i className="bi bi-grid-fill me-2" />
                            Explore Products
                        </button>
                        <a href="mailto:sales@mhe.co.za" className="hero-btn-outline">
                            <i className="bi bi-envelope me-2" />
                            Contact Us
                        </a>
                    </div>
                </div>
                <div className="hero-scroll-hint">
                    <i className="bi bi-chevron-double-down" />
                </div>
            </section>

            {/* ── Stats Bar ── */}
            <section className="stats-bar">
                <div className="stats-bar-inner">
                    <div className="stat-item">
                        <i className="bi bi-box-seam stat-icon" />
                        <div>
                            <span className="stat-value">{products.length}+</span>
                            <span className="stat-label">Products</span>
                        </div>
                    </div>
                    <div className="stat-divider" />
                    <div className="stat-item">
                        <i className="bi bi-globe2 stat-icon" />
                        <div>
                            <span className="stat-value">2</span>
                            <span className="stat-label">Global Offices</span>
                        </div>
                    </div>
                    <div className="stat-divider" />
                    <div className="stat-item">
                        <i className="bi bi-award stat-icon" />
                        <div>
                            <span className="stat-value">30+</span>
                            <span className="stat-label">Years Experience</span>
                        </div>
                    </div>
                    <div className="stat-divider" />
                    <div className="stat-item">
                        <i className="bi bi-headset stat-icon" />
                        <div>
                            <span className="stat-value">24/7</span>
                            <span className="stat-label">Support</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Featured Products ── */}
            <section className="featured-section">
                <div className="featured-header">
                    <span className="featured-eyebrow">Our Catalogue</span>
                    <h2 className="featured-title">Featured Products</h2>
                    <p className="featured-desc">
                        Explore our range of high-quality electronics and industrial components.
                    </p>
                </div>

                <div className="featured-carousel-wrap">
                    {loading ? (
                        <div className="featured-loading">
                            <span className="loaderWhite" style={{ borderColor: '#cc0000 #cc0000 transparent transparent' }} />
                            <p>Loading products&hellip;</p>
                        </div>
                    ) : sortedFeaturedProducts.length > 0 && imageDirectory ? (
                        <OwlCarousel
                            className="owl-theme"
                            loop
                            margin={20}
                            nav
                            dots={false}
                            responsive={{
                                0: { items: 1 },
                                600: { items: 2 },
                                1000: { items: 3 },
                                1400: { items: 4 },
                            }}
                        >
                            {sortedFeaturedProducts.map((product, index) => (
                                <Link
                                    key={product.id || `product-${index}`}
                                    className="fp-card-link item"
                                    to={`/ProdView/${product.proD_NAME}`}
                                    state={{
                                        imgDirectory: imageDirectory,
                                        docsDirectory: docsDirectory,
                                        prod: product
                                    }}
                                >
                                    <div className="fp-card">
                                        <div className="fp-card-img-wrap">
                                            <img
                                                src={`${imageDirectory}/${product.imG_PATH || 'default.jpg'}`}
                                                alt={product.proD_NAME}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = fallbackImage;
                                                }}
                                            />
                                        </div>
                                        <div className="fp-card-body">
                                            <h3 className="fp-card-name">{product.proD_NAME}</h3>
                                            <span className="fp-card-cta">
                                                View Details <i className="bi bi-arrow-right ms-1" />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </OwlCarousel>
                    ) : (
                        !loading && (
                            <p className="text-center text-muted py-4">No products available.</p>
                        )
                    )}
                </div>

                <div className="text-center mt-4 pb-5">
                    <button className="view-all-btn" onClick={() => navToPage('/Products', 0)}>
                        View All Products <i className="bi bi-arrow-right ms-2" />
                    </button>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="home-footer">
                <div className="footer-inner">
                    <div className="footer-brand">
                        <img src={MHELogo} alt="MHE Electronics" className="footer-logo" />
                        <p className="footer-copy">&copy; {new Date().getFullYear()} MHE Electronics. All Rights Reserved.</p>
                    </div>

                    <div className="footer-offices">
                        <div className="footer-office">
                            <h6 className="footer-office-title">
                                <i className="bi bi-geo-alt-fill me-2 text-danger" />South Africa
                            </h6>
                            <p>49 Alexander Road, Westmead, Pinetown, KZN, 3608</p>
                            <p><i className="bi bi-telephone me-2" />+27 31 780 2560</p>
                            <p>
                                <i className="bi bi-envelope me-2" />
                                <a href="mailto:sales@mhe.co.za" className="footer-link">sales@mhe.co.za</a>
                            </p>
                        </div>

                        <div className="footer-office">
                            <h6 className="footer-office-title">
                                <i className="bi bi-geo-alt-fill me-2 text-danger" />United Kingdom
                            </h6>
                            <p>Fairstead House, Erratts Hill, Cowlinge, Newmarket, CB8 9QD</p>
                            <p><i className="bi bi-telephone me-2" />+44 1440 820381</p>
                            <p>
                                <i className="bi bi-envelope me-2" />
                                <a href="mailto:mike@mheuk.co.uk" className="footer-link">mike@mheuk.co.uk</a>
                            </p>
                        </div>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export default Home;
